import React from "react";
import { X, Check, Sparkles, Layers, Box, Ruler, Palette } from "lucide-react";

export default function SetBreakdownModal({ product, onClose }) {
  if (!product) return null;

  // New schema: product.complete_set = { colors, taan_count, gaz_per_taan, total_gaz, min_order_sets }
  const cs = product.complete_set || {};
  const colorsList = Array.isArray(cs.colors) && cs.colors.length > 0
    ? cs.colors
    : (product.available_colors || []);
  const taanCount = Number(cs.taan_count) || 0;
  const gazPerTaan = Number(cs.gaz_per_taan) || 0;
  const totalGaz = Number(cs.total_gaz) || (taanCount * gazPerTaan) || 0;
  const totalColors = colorsList.length;
  const minOrderSets = Number(cs.min_order_sets) || 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div
        data-testid="set-breakdown-modal"
        className="bg-[#FAF8F5] rounded-2xl max-w-lg w-full border border-[#D4AF37]/50 shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-[#1C1917] text-white p-5 flex items-center justify-between border-b-2 border-[#D4AF37]">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D4AF37]" />
            <div>
              <h3 className="font-heading text-lg font-bold text-white">Complete Set Breakdown</h3>
              <p className="text-xs text-[#E6C687]">Anwer Fancy Lace Signature Pack Structure</p>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-set-modal-btn"
            className="text-stone-400 hover:text-white p-1 rounded-full hover:bg-stone-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
          {/* Product Summary */}
          <div className="bg-white p-4 rounded-xl border border-[#E5E0D8]">
            <h4 className="font-semibold text-stone-900 text-sm">{product.name}</h4>
            <p className="text-xs text-stone-500 mt-1">Category: {product.category}</p>
            {product.design_code && (
              <p className="text-xs text-[#996515] mt-1 font-semibold">Design Code: {product.design_code}</p>
            )}
          </div>

          {/* Complete Set Metrics — Colors / Taan / Gaz */}
          <div>
            <h5 className="text-xs font-bold uppercase tracking-wider text-stone-700 mb-2.5 flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-[#D4AF37]" />
              What's in 1 Complete Set:
            </h5>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <div
                data-testid="set-modal-total-colors"
                className="p-3 bg-white rounded-lg border border-[#E5E0D8] text-center"
              >
                <div className="flex items-center justify-center text-[#996515] mb-1">
                  <Palette className="w-4 h-4" />
                </div>
                <div className="text-lg font-bold text-stone-900">{totalColors}</div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">Total Colors</div>
              </div>
              <div
                data-testid="set-modal-taan-count"
                className="p-3 bg-white rounded-lg border border-[#E5E0D8] text-center"
              >
                <div className="flex items-center justify-center text-[#996515] mb-1">
                  <Layers className="w-4 h-4" />
                </div>
                <div className="text-lg font-bold text-stone-900">{taanCount}</div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">Taan</div>
              </div>
              <div
                data-testid="set-modal-gaz-per-taan"
                className="p-3 bg-white rounded-lg border border-[#E5E0D8] text-center"
              >
                <div className="flex items-center justify-center text-[#996515] mb-1">
                  <Ruler className="w-4 h-4" />
                </div>
                <div className="text-lg font-bold text-stone-900">{gazPerTaan}</div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">Gaz / Taan</div>
              </div>
              <div
                data-testid="set-modal-total-gaz"
                className="p-3 bg-white rounded-lg border border-[#E5E0D8] text-center"
              >
                <div className="flex items-center justify-center text-[#996515] mb-1">
                  <Box className="w-4 h-4" />
                </div>
                <div className="text-lg font-bold text-stone-900">{totalGaz}</div>
                <div className="text-[10px] uppercase tracking-wider text-stone-500">Total Gaz</div>
              </div>
            </div>
            {minOrderSets > 0 && (
              <p className="text-[11px] text-stone-500 mt-2">
                Minimum order: <span className="font-bold text-stone-800">{minOrderSets} Complete Set{minOrderSets > 1 ? "s" : ""}</span>
              </p>
            )}
          </div>

          {/* Colors in Pack */}
          {colorsList.length > 0 && (
            <div className="bg-[#F5F2EB] p-4 rounded-xl border border-[#E5E0D8]">
              <h5 className="text-xs font-bold uppercase tracking-wider text-stone-800 mb-2">
                Colors Included in Complete Set
              </h5>
              <div className="flex flex-wrap gap-2">
                {colorsList.map((c, idx) => (
                  <span
                    key={`${c}-${idx}`}
                    className="px-2.5 py-1 bg-white rounded-md border border-[#D4AF37]/40 text-xs font-medium text-stone-800 flex items-center gap-1 shadow-sm"
                  >
                    <Check className="w-3 h-3 text-[#D4AF37]" />
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Pricing Notice */}
          <div className="text-xs text-stone-600 bg-amber-50/70 p-3 rounded-lg border border-amber-200/60 leading-relaxed">
            <span className="font-bold text-amber-900">Note: </span>
            At Anwer Fancy Lace, every product is packed as a Complete Set with pre-defined Colors, Taan count and Gaz yardage — ensuring consistent dye, identical sitara luster, and matching finishing across the set.
          </div>
        </div>

        {/* Footer */}
        <div className="bg-[#F5F2EB] p-4 border-t border-[#E5E0D8] flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-bold rounded-lg transition"
          >
            Close Breakdown
          </button>
        </div>
      </div>
    </div>
  );
}
